// Earcons and waits for Car mode, synthesized as WAV so they play through the
// same <audio> element as the speech: the steering-wheel buttons only reach a
// page while its media element keeps playing, so even the pauses are "tracks".

const SAMPLE_RATE = 22050;
const FADE_MS = 8;

interface Tone {
    /** Start offset in ms. */
    at: number;
    ms: number;
    freq: number;
    gain: number;
}

function renderWav(durationMs: number, tones: Tone[]): Blob {
    const samples = new Float32Array(Math.ceil(SAMPLE_RATE * durationMs / 1000));
    const fade = SAMPLE_RATE * FADE_MS / 1000;

    for (const tone of tones) {
        const start = Math.floor(SAMPLE_RATE * tone.at / 1000);
        const length = Math.floor(SAMPLE_RATE * tone.ms / 1000);
        for (let i = 0; i < length && start + i < samples.length; i++) {
            const envelope = Math.min(1, i / fade, (length - i) / fade);
            samples[start + i] += Math.sin(2 * Math.PI * tone.freq * i / SAMPLE_RATE) * tone.gain * envelope;
        }
    }

    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    const writeString = (offset: number, text: string) => {
        for (let i = 0; i < text.length; i++)
            view.setUint8(offset + i, text.charCodeAt(i));
    };

    writeString(0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, SAMPLE_RATE, true);
    view.setUint32(28, SAMPLE_RATE * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, samples.length * 2, true);
    samples.forEach((sample, i) => {
        const clamped = Math.max(-1, Math.min(1, sample));
        view.setInt16(44 + i * 2, clamped * 0x7fff, true);
    });

    return new Blob([buffer], { type: 'audio/wav' });
}

function ticks(durationMs: number, everyMs: number, freq: number, gain: number): Tone[] {
    const result: Tone[] = [];
    for (let at = everyMs / 2; at < durationMs; at += everyMs)
        result.push({ at, ms: 25, freq, gain });
    return result;
}

const LISTENING_MS = 6000;

export interface CarSounds {
    newCard: string;
    gap: string;
    /** "Your turn": played when the question is over and the answer is awaited. */
    yourTurn: string;
    /** Looped while listening for an answer or a command. */
    listening: string;
    correct: string;
    wrong: string;
    loading: string;
    sessionEnd: string;
}

let sounds: CarSounds | undefined;

export function carSounds(): CarSounds {
    if (sounds !== undefined)
        return sounds;

    const url = (durationMs: number, tones: Tone[]) => URL.createObjectURL(renderWav(durationMs, tones));

    sounds = {
        newCard: url(450, [
            { at: 0, ms: 160, freq: 660, gain: 0.35 },
            { at: 170, ms: 260, freq: 880, gain: 0.35 },
        ]),
        gap: url(260, [{ at: 30, ms: 180, freq: 1000, gain: 0.3 }]),
        yourTurn: url(380, [
            { at: 0, ms: 140, freq: 523, gain: 0.3 },
            { at: 150, ms: 200, freq: 784, gain: 0.3 },
        ]),
        // A near-silent wait: a faint tick every two seconds says "still listening".
        listening: url(LISTENING_MS, ticks(LISTENING_MS, 2000, 1200, 0.05)),
        correct: url(420, [
            { at: 0, ms: 130, freq: 784, gain: 0.35 },
            { at: 140, ms: 260, freq: 1046, gain: 0.35 },
        ]),
        wrong: url(520, [
            { at: 0, ms: 180, freq: 330, gain: 0.4 },
            { at: 190, ms: 300, freq: 247, gain: 0.4 },
        ]),
        // Looped while the next clip is generated: two quiet seconds, then a soft pulse.
        loading: url(3000, [{ at: 2000, ms: 300, freq: 440, gain: 0.12 }]),
        sessionEnd: url(900, [
            { at: 0, ms: 200, freq: 1046, gain: 0.3 },
            { at: 220, ms: 200, freq: 784, gain: 0.3 },
            { at: 440, ms: 400, freq: 523, gain: 0.3 },
        ]),
    };
    return sounds;
}
